import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { reelService } from 'api/services/ReelService';
import type { ReelViewModel } from 'api/types/reel';

export interface UseListingReelsResult {
  reels: ReelViewModel[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Normalize a raw backend reel item into the UI-friendly {@link ReelViewModel} shape.
 * Tolerates PascalCase (C# default) and camelCase payloads because both appear in the codebase.
 */
function normalizeReelItem(raw: unknown): ReelViewModel {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const listingIdRaw = o.listingId ?? o.ListingId;
  return {
    id: String(o.id ?? o.Id ?? ''),
    videoUrl: String(o.videoUrl ?? o.VideoUrl ?? ''),
    userId: String(o.userId ?? o.UserId ?? ''),
    createdAt: (() => {
      const v = o.createdAt ?? o.CreatedAt ?? o.createdAtUtc ?? o.CreatedAtUtc;
      return typeof v === 'string' ? v : null;
    })(),
    listingId: typeof listingIdRaw === 'string' && listingIdRaw.length > 0 ? listingIdRaw : null,
  };
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; Message?: string } | undefined;
    const serverMessage = data?.message ?? data?.Message;
    if (typeof serverMessage === 'string' && serverMessage.trim().length > 0) return serverMessage;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Reeller yüklenirken bir hata oluştu.';
}

/**
 * Fetch the reels linked to a listing (feature 009, US3).
 * Re-fetches whenever {@code listingId} changes; no-ops when falsy (e.g. before the
 * listing detail page resolves its own route id).
 */
export function useListingReels(
  listingId: string | null | undefined
): UseListingReelsResult {
  const [reels, setReels] = useState<ReelViewModel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((v) => v + 1), []);

  useEffect(() => {
    if (!listingId) {
      setReels([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    reelService
      .getByListingId(listingId)
      .then((result) => {
        if (cancelled) return;
        if (result.isSuccess) {
          const rawList = Array.isArray(result.data) ? result.data : [];
          setReels(rawList.map((item) => normalizeReelItem(item)).filter((r) => r.id && r.videoUrl));
        } else {
          setError(result.message ?? 'Reeller yüklenemedi.');
          setReels([]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractErrorMessage(err));
        setReels([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [listingId, refreshToken]);

  return { reels, loading, error, refresh };
}
